import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { BigButton, PublicLoading, PublicMessage, PublicShell } from "@/components/checkins/public/PublicShell";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/public/checkins/manage`;

function Confirm({ view, onDone, onCancel, token }) {
  const [all, setAll] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  async function stop() {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/${token}/opt-out`, { confirm: true, scope: all ? "all" : "template", reason: reason.trim() || null });
      onDone(r.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Something went wrong — please try again.");
      setBusy(false);
    }
  }
  return (
    <div className="space-y-5 rounded-2xl border border-zinc-200 bg-white p-6" data-testid="manage-confirm">
      <h2 className="font-display text-2xl font-bold text-zinc-950">Confirm you want to stop progress review emails</h2>
      {view.other_active > 0 && (
        <label className="flex min-h-[44px] items-center gap-3 text-lg">
          <input type="checkbox" className="h-6 w-6" checked={all} onChange={(e) => setAll(e.target.checked)} data-testid="manage-all-checkbox" />
          Also stop all my other recurring clinical progress reviews
        </label>
      )}
      <label className="block text-lg">
        <span className="text-zinc-700">Would you like to tell us why? (optional)</span>
        <textarea rows={3} maxLength={300} value={reason} onChange={(e) => setReason(e.target.value)} data-testid="manage-reason"
          className="mt-2 w-full rounded-xl border-2 border-zinc-300 p-3 text-lg focus:border-zinc-900 focus:outline-none focus-visible:ring-4 focus-visible:ring-sky-400" />
      </label>
      {err && <p className="text-base font-semibold text-red-700" role="alert">{err}</p>}
      <div className="flex flex-wrap gap-3">
        <BigButton onClick={stop} disabled={busy} data-testid="manage-confirm-btn">{busy ? "Stopping…" : "Yes, stop progress review emails"}</BigButton>
        <BigButton variant="secondary" onClick={onCancel} data-testid="manage-cancel-btn">Go back</BigButton>
      </div>
    </div>
  );
}

export default function PublicCheckinManage() {
  const { token } = useParams();
  const [view, setView] = useState(null);
  const [err, setErr] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [done, setDone] = useState(null);
  useEffect(() => {
    axios.get(`${API}/${token}`).then((r) => setView(r.data)).catch((e) => setErr(e?.response?.data?.detail || "This link isn't available."));
  }, [token]);
  if (err) return <PublicShell testId="public-manage-page"><PublicMessage title="Link unavailable" testId="manage-unavailable"><p>{err}</p></PublicMessage></PublicShell>;
  if (!view) return <PublicLoading />;
  const stopped = done || view.status === "opted_out";
  return (
    <PublicShell clinic={view.clinic} testId="public-manage-page">
      {stopped ? (
        <PublicMessage title="Progress review emails stopped" testId="manage-done">
          <p>You won&apos;t receive any more {view.template_name || "progress review"} invitations, reminders or automated summaries.</p>
          <p>Appointment confirmations, billing notices and messages from your care team are not affected. To restart progress reviews later, just let your exercise team know.</p>
        </PublicMessage>
      ) : (
        <div className="space-y-6">
          <div>
            <h1 className="font-display text-3xl font-bold tracking-tight text-zinc-950">Manage progress review emails</h1>
            <p className="mt-3 text-lg leading-relaxed text-zinc-800">Hi {view.first_name}. You currently receive the <strong>{view.template_name}</strong> every few weeks.</p>
          </div>
          <div className="rounded-2xl border border-zinc-200 bg-white p-5 text-lg leading-relaxed text-zinc-800" data-testid="manage-scope">
            Stopping progress review emails stops recurring exercise progress review invitations, reminders and automated progress review
            summaries. It does <strong>not</strong> stop appointment confirmations, billing notices, direct messages from your
            care team or other essential clinical communications.
          </div>
          {confirming
            ? <Confirm view={view} token={token} onDone={setDone} onCancel={() => setConfirming(false)} />
            : <BigButton onClick={() => setConfirming(true)} data-testid="manage-stop-btn">Stop progress review emails</BigButton>}
        </div>
      )}
    </PublicShell>
  );
}
